import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import CustomInput from '../../../CustomComponents/FormInputs';
import Arrow from '../../../../assets/images/arrow.png';
import Modal from '../../../CustomComponents/Modal';
import addLineBreaks, { displayErrorMessage } from '../../../../utils';
import Card from '../../../CustomComponents/Card';
import VerifyEmailCode from '../../Signup/EmailVerification';
import { GetApiHeadersWithOutBearer, GetApiHeadersWithBearer, GetRequest, PostRequest } from '../../../../network/NetworkUtils';
import { EMAIL_OTP_GENERATION, STATUS_SUCCESS_CODE, STUDENT_SIGNUP_EMAIL_ACCOUNT, STUDENT_SIGNUP_UNIVERSITY_SELECTION } from '../../../../network//URLConstants';
import { useDispatch, useSelector } from 'react-redux';
import Loader from "../../../CustomComponents/Loader";
import { appTheme, checkEmailExtension, isEmailValid as validateEmailAddress, retrieveData } from '../../../../helpers/utils/Utils';
import { fetchConfigarationData } from '../../../../helpers/utils/Utils';
import CustomAlertModel from '../../../CustomComponents/CustomAlertModel';
import { useNavigate } from 'react-router';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from 'moment';
import { AsyncPaginate } from 'react-select-async-paginate';

// API functions for fetching partner schools data
const fetchPartnerSchools = async (searchTerm, page, accessToken) => {
  const deviceId = await retrieveData('updatedDeviceId');
  const headers = await GetApiHeadersWithBearer(deviceId, accessToken);
  const url = STUDENT_SIGNUP_UNIVERSITY_SELECTION(searchTerm, 20, page);
  const response = await GetRequest(url, headers);
  const responseObj = JSON.parse(response);

  if (responseObj.ResponseCode === STATUS_SUCCESS_CODE) {
    const universities = responseObj.Response.Universities.map(uni => ({
      label: uni.Name,
      value: uni.ID
    }));

    if (universities.length === 0) {
      universities.push({ label: "Other", value: 0 });
    }

    return {
      options: universities,
      hasMore: responseObj.Response.Universities.length > 0,
      additional: {
        page: page + 1,
      },
    };
  }
};

const loadPartnerSchoolOptions = async (inputValue, _, additional, accessToken) => {
  const searchTerm = inputValue === "" ? "a" : inputValue;
  const page = additional.page || 1;
  return await fetchPartnerSchools(searchTerm, page, accessToken);
};

// const appConfigData = await fetchConfigarationData();
export const StudentEmailVerfication = ({ userEmail, openAccessCodewithemail, backtoUserType, openUniversitySelection, onStudentDetailsComplete }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const appConfigData = useSelector(state => state.appConfigData);
  const navigate = useNavigate();


  const schema = yup.object().shape({
    email: yup
      .string()
      .required(t('please_enter_a_valid_email_address'))
      .email(t('please_enter_a_valid_email_address')),
    universityId: yup
      .number()
      .nullable()
      .test('university-required', t('please_select_school_name'), function(value) {
        const { universityName } = this.parent;
        // Valid if either universityId is selected OR universityName is filled (when universityId is 0)
        return value !== null || (universityName && universityName.length >= 5);
      }),
    universityName: yup
      .string()
      .when('universityId', {
        is: 0,
        then: (schema) => schema.min(5, t('university_name_min_length')).required(t('please_enter_university_name')),
        otherwise: (schema) => schema.nullable(),
      }),
    graduationDate: yup
      .date()
      .nullable()
      .required(t('please_select_graduation_date')),
  });

  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const accessToken = useSelector(state => state.accessToken);
  const sessionId = useSelector(state => state.sessionId);
  const userId = useSelector(state => state.userId);
  const [message, setMessage] = useState('');
  const [validEmail, setValidEmail] = useState(false);
  const studentEmailAddress = useSelector(state => state.studentEmailAddress);

  // Validation states for icons
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [isUniversityValid, setIsUniversityValid] = useState(false);
  const [isUniversityNameValid, setIsUniversityNameValid] = useState(false);
  const [isGraduationDateValid, setIsGraduationDateValid] = useState(false);

  // University and Graduation Date states
  const [selected, setSelected] = useState(null);
  const [universityName, setUniversityName] = useState('');
  const [selectedDate, setSelectedDate] = useState(null);
  const [universityInput, setUniversityInput] = useState(false);
  const [isPartnerUniversity, setIsPartnerUniversity] = useState(false);
  const [emailValidated, setEmailValidated] = useState(false);
  const accessTokenToApi = useSelector(state => state.accessToken);
  const today = new Date();
  const minDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);
  const currentYear = new Date().getFullYear();

  const signUpkudzuPlusTrue = useSelector(state => state.isSignUpKudzuplusTrue);  
  const appThemeData = appTheme(signUpkudzuPlusTrue)

  useEffect(() => { }, [sessionId, userId])


  // On submit
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    clearErrors,
    setError,
    setValue,
    reset,
    watch,
    trigger
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(schema),
    defaultValues: {
      email: '',
      universityId: null,
      universityName: '',
      graduationDate: null,
    },
  });

  // Watch the email field to keep our state in sync
  const watchedEmail = watch('email');

  // Sync the email state with the form value
  useEffect(() => {
    if (watchedEmail !== undefined) {
      setEmail(watchedEmail);
    }
  }, [watchedEmail]);

  const GenerateEmailOtp = async () => {
    setMessage("");

    // Trigger form validation using react-hook-form first
    const isFormValid = await trigger();

    // Additional validation for email extension (custom validation not in yup schema)
    if (email && email.trim() !== '') {
      const isValidExtension = checkEmailExtension(email);
      if (!isValidExtension) {
        setError('email', {
          type: 'manual',
          message: t('please_enter_student_email_address'),
        });
        setIsEmailValid(false);
        return;
      }
    }

    // Update validation state icons based on current values
    // Note: errors object is updated after trigger() completes
    const emailErrors = !email || email.trim() === '' || errors.email;
    setIsEmailValid(!emailErrors);
    setIsUniversityValid(!!selected || !!universityName);
    setIsUniversityNameValid(selected !== 0 || (universityName && universityName.length >= 5));
    setIsGraduationDateValid(!!selectedDate);

    // If form is not valid, stop here (errors will be displayed automatically)
    if (!isFormValid) {
      return;
    }

    // All validations passed, now validate email with backend
    createAccountWithStudentEmail(email);
  };


  async function createAccountWithStudentEmail(email) {
    const deviceId = await retrieveData('updatedDeviceId')
    let headers = await GetApiHeadersWithOutBearer(deviceId);
    setIsLoading(true)
    var requestObject = {
      "applicationId": appConfigData.CLIENT_ID,
      "sEmail": email,
      "language": "en",
      "isLoginid": true
    }
    if (userEmail) {
      requestObject.isLoginid = false;
    }
    var response = await PostRequest(STUDENT_SIGNUP_EMAIL_ACCOUNT(userId), requestObject, headers);
    let responseObj = JSON.parse(response);
    if (responseObj.ResponseCode === STATUS_SUCCESS_CODE) 
    {     
        const isPartnerUniversityStatus=responseObj.Response.isPartnerUniversity;
        setIsPartnerUniversity(isPartnerUniversityStatus);
        setIsLoading(false); 
        setEmailValidated(true);

        if(isPartnerUniversityStatus){
          const sessionIdPayload = { sessionId: responseObj.Response.sessionId }
          dispatch({ type: 'SESSION_ID', payload: sessionIdPayload });
          openAccessCodewithemail();
        }else{
          // All student details collected in single form, go directly to next step
          // Skip the universitySelection screen since we already have that data
          if (onStudentDetailsComplete) {
            onStudentDetailsComplete();
          } else {
            // Fallback to old behavior if prop not provided
            openUniversitySelection();
          }
        } 

      } else {
        setIsLoading(false);
        var arrMessages = responseObj.Messages;
        var message = arrMessages[0];
        var displayText = message.DisplayText;
        setMessage(displayText)
      }
  }



  const handleChange = async (event) => {
    setMessage('');
    const { name, value } = event.target;

    // Update form value
    setValue('email', value, { shouldValidate: true });

    // Validate email format and extension
    if (!value || value.trim() === '') {
      // Empty field
      setValidEmail(false);
      setIsEmailValid(false);
      clearErrors('email');
      return;
    }

    const emailValidation = validateEmailAddress(value);
    const isValidExtension = checkEmailExtension(value);

    // Email is valid if validateEmailAddress doesn't return error strings AND extension is valid
    // validateEmailAddress returns 'empty' or 'not valid' for errors, or the email itself if valid
    const isEmailFormatValid = emailValidation !== 'empty' && emailValidation !== 'not valid';

    if (isEmailFormatValid && isValidExtension) {
      setValidEmail(true);
      setIsEmailValid(true);
      clearErrors('email');
      dispatch({ type: 'STUDENT_EMAIL_VALUE', payload: { studentEmailAddress: value } });
    } else {
      setValidEmail(false);
      setIsEmailValid(false);
      if (!isValidExtension && isEmailFormatValid) {
        // Valid email format but not a student email
        setError('email', {
          type: 'manual',
          message: t('please_enter_student_email_address'),
        });
      }
    }
  };

  const handleEmailBlur = () => {
    // Trigger validation on blur to show error state when field is left empty or invalid
    if (!email || email.trim() === '') {
      setIsEmailValid(false);
      setError('email', {
        type: 'manual',
        message: t('please_enter_a_valid_email_address'),
      });
    } else {
      // Re-validate on blur
      const emailValidation = validateEmailAddress(email);
      const isValidExtension = checkEmailExtension(email);
      const isEmailFormatValid = emailValidation !== 'empty' && emailValidation !== 'not valid';

      if (!isEmailFormatValid) {
        setIsEmailValid(false);
        setError('email', {
          type: 'manual',
          message: t('please_enter_a_valid_email_address'),
        });
      } else if (!isValidExtension) {
        setIsEmailValid(false);
        setError('email', {
          type: 'manual',
          message: t('please_enter_student_email_address'),
        });
      }
    }
  };

  // University and graduation date handlers
  const handleDateChange = (date) => {
    setSelectedDate(date);
    setValue('graduationDate', date, { shouldValidate: true });

    // Validate: date must be selected and in the future
    if (date && date >= minDate) {
      setIsGraduationDateValid(true);
      clearErrors('graduationDate');
    } else {
      setIsGraduationDateValid(false);
    }

    if (date) {
      const formattedDate = moment(date).format("yyyy-MM");
      dispatch({ type: 'GRADUATION_DATE', payload: { graduationDate: `${formattedDate}-01` } });
    }
  }

  const handleSelectChange = (value) => {
    setSelected(value);
    setValue('universityId', value, { shouldValidate: true });
    setIsUniversityValid(value !== null && value !== undefined);

    // Clear university error when a valid selection is made
    if (value !== null && value !== undefined) {
      clearErrors('universityId');
    }

    dispatch({ type: 'STUDENT_UNIVERSITY_CODE', payload: { studentUniversityCode: value } });
    if (value === 0) {
      setUniversityInput(true);
      setUniversityName('');
      setIsUniversityNameValid(false);
      setValue('universityName', '', { shouldValidate: true });
    } else {
      setUniversityInput(false);
      setIsUniversityNameValid(true); // Valid when a partner university is selected
      setValue('universityName', '', { shouldValidate: false });
      clearErrors('universityName');
    }
  }

  const handleUniversityInputChange = (e) => {
    const universityNameVal = e.target.value;
    setUniversityName(universityNameVal);
    setValue('universityName', universityNameVal, { shouldValidate: true });
    setIsUniversityNameValid(universityNameVal.length >= 5);
    if (universityNameVal) {
      dispatch({ type: 'STUDENT_UNIVERSITY_NAME', payload: { studentUniversityName: universityNameVal } });
    }
  }

  const customStyles = {
    option: (provided, state) => ({
      ...provided,
      color: 'blue',
      padding: '18px',
      color: state.isFocused && !state.isSelected ? 'white' : provided.color,
      color: state.isSelected && !state.isFocused ? 'black' : provided.color,
      backgroundColor: state.isFocused ? '#94C3E8' : provided.backgroundColor,
      backgroundColor: state.isSelected ? '#94C3E8' : state.isFocused ? '#94C3E8' : null,
    })
  }

  const handleBack = () => {
    dispatch({ type: 'STUDENT_EMAIL_VALUE', payload: { studentEmailAddress: "" } });
    reset(); 
    clearErrors(); 
    setMessage('');
    setEmail('');
    // Reset validation states
    setIsEmailValid(false);
    setIsUniversityValid(false);
    setIsUniversityNameValid(false);
    setIsGraduationDateValid(false);
    backtoUserType();
  };

  // No auto-fill - keep email field clean for user input

  // const closeCustomAlertModal = () => {
  //   setBtn1Text('')
  //   setShowAlertModal(false);
  // }
  // const onAcptBtnClick = async () => {
  //   setShowAlertModal(false)
  //   if (btn1Text === t('login')) {
      

  //   }
  // }
  // const onCancelBtnClick = () => {
  //   setBtn1Text('')
  //   setShowAlertModal(false)
  // }
  return (
    <>
      <div className="sm:px-[6%] md:py-10 sm:py-12 flex flex-wrap sm:flex-nowrap scroll-mt-0" onClick={(e)=> { e.stopPropagation();  
    e.preventDefault()}}>
        <Card cardHeight={"950px"}
               isWhiteForSignup={appThemeData.isWhiteBgForCard}
               isDarkForKudzu={appThemeData.isDarkBgForCard}
               backgroundColor={'--modalcard-bg'}
               showBackButton={true}
               onBackClick={handleBack}
               backButtonText={t('back')}
          heading=
          {
            <>
             <span className='font-serif-pro  text-[#2965FB] sm:text-[var(--signup-flow-heading-text)] lg:text-[31px]
             md:text-[26px] sm:text-[22px] text-[25px] lg:leading-[38px]
              md:leading-[33px] sm:leading-[29px] leading-[31px]'>{t('student_selction_heading1')} </span>
              <span className='font-serif-pro sm:text-[var(--text-clr-secondary)] text-[var(--text-clr-secondary3)] lg:text-[31px]
             md:text-[26px] sm:text-[22px] text-[25px] lg:leading-[38px]
              md:leading-[33px] sm:leading-[29px] leading-[31px]'>{t('student_acces_code_sub-heading2')}</span>
              <span className='font-serif-pro sm:text-[var(--text-clr-secondary)]
               text-[var(--text-clr-secondary3)] lg:text-[31px]
             md:text-[26px] sm:text-[22px] text-[25px] lg:leading-[38px] md:leading-[33px] sm:leading-[29px] leading-[31px]'>
              {addLineBreaks(t('student_email_account_headin2'))}</span>
              
            </>}
            
          // subheading={addLineBreaks(t('student_email_account_headin3'), SubTittleStylesModal)}
          header={true} footer={true} CardRadius={true} >
          {/* <p className="lg:my-[2rem] md:my-[1.5rem] my-[1.5rem] lg:text-[20px] lg:w-[451px] md:text-[20px] sm:text-[12px] text-[20px] font-hanken-grotesk font-medium sm:mx-0 mx-4 text-[var(--text-clr-secondary3)]">
            {t('student_email_account_headin1-heading')}</p> */}


          <p className='sm:block hidden text-[var(--modal-text-heading-clr)] font-serif-pro lg:leading-[36px] md:leading-[24px] 
            leading-[31px] font-[400] lg:text-[32px] md:text-[29px] sm:text-[22px] text-[25px]'>
            <span className='text-[var(--modal-text-highlight)]'>
              {t('student_selction_heading1')}</span>
            <span className='text-[var(--accConfig-color)]'> {t('university_details1_2')}</span>
          </p>

          <p className='lg:text-[20px] md:text-[20px] sm:text-[12px] 
          text-[14px] lg:leading-7 md:leading-[22px] sm:leading-[22px] 
          leading-7 font-hanken-grotesk text-[var(--accConfig-color)]
           font-medium mt-4'>{t('proudToPartnerSchool')}</p>

          <div className="flex flex-col  w-full h-246 mt-8">
            <div className="flex flex-col">
              <CustomInput
                name="email"
                label={t('student_email_address')}
                placeholder={t("student_email_address_placeholder")}
                register={register}
                error={errors.email}
                type="text"
                onChange={handleChange}
                onBlur={handleEmailBlur}
                isValid={isEmailValid}
                autoFocus={true}
                required={true}
                showValidity={true}
                isErrorShow={true}
              />
              {message &&
                <div className='mb-2'>
                  {displayErrorMessage(message)}
                </div>
              }

              {/* University Field - Always visible */}
              <div className='mb-4 relative' id="bgforDatePicker">
                <p className='font-hanken-grotesk uppercase lg:text-[13px] md:text-[9px] 
                  sm:text-[9px] text-[13px] font-[600] tracking-[2px] md:pb-2 pb-2
                  text-[var(--text-clr-textarea)]'>{t('school_name')} <span className='text-red-500'>*</span></p>
                <div className='flex w-full items-baseline relative'>
                  <AsyncPaginate
                    placeholder='Select'
                    debounceTimeout={300}
                    loadOptions={(inputValue, _, additional) => 
                      loadPartnerSchoolOptions(inputValue, _, additional, accessTokenToApi)
                    }
                    onChange={(data) => {
                      handleSelectChange(data.value);
                      setSelected(data.value)
                    }}
                    additional={{ page: 1 }}
                    isMulti={false}
                    styles={customStyles}
                  />

                  {/* Validation Icon for University */}
                  <div className="absolute right-[-2rem] top-5">
                    {isUniversityValid && selected !== null && (
                      <img src={appThemeData.Check} className="w-4 h-4" alt="Valid" />
                    )}
                    {!isUniversityValid && errors.universityId && (
                      <img src={appThemeData.ExclamationError} className="w-4 h-4" alt="Error" />
                    )}
                  </div>
                </div>
                {/* Error message for university field */}
                {errors.universityId && (
                  <div className='mt-2'>
                    {displayErrorMessage(errors.universityId.message)}
                  </div>
                )}
                {universityInput &&
                  <div className='mt-5 mb-4'>
                    <CustomInput
                      name="universityName"
                      label={t('school_name')}
                      register={register}
                      type="text"
                      required={true}
                      onChange={handleUniversityInputChange}
                      showValidity={true}
                      isValid={isUniversityNameValid}
                      error={errors.universityName}
                    />
                  </div>
                }
              </div>

              {/* Graduation Date Field - Always visible */}
               <div className='mt-1 mb-4 relative'>
                <label className="font-hanken-grotesk text-[var(--text-clr-textarea)] uppercase lg:text-[13px] md:text-[9px] 
                  sm:text-[9px] text-[13px] font-[600] tracking-[2px] md:pb-2 pb-2 inline-block" 
                  htmlFor={'ExpectedGraduationyear'}>{t('ExpectedGraduationyear')} <span className='text-red-500'>*</span></label>
                <div className="relative">
                  <DatePicker
                    onChange={handleDateChange}
                    dateFormat="MM/yyyy"
                    name="grduationYear"
                    label={t('ExpectedGraduationyear')}
                    className='font-hanken-grotesk text-[var(--input-text)] px-4 bg-[var(--input-bg)] placeholder-[var(--input-text)] 
                      py-2 text-sm font-medium focus:outline-none w-full h-[60px]'
                    showMonthDropdown
                    showYearDropdown
                    selected={selectedDate}
                    placeholderText='MM/YYYY'
                    autoComplete='off'
                    minDate={minDate}
                    yearDropdownItemNumber={100}
                    scrollableYearDropdown
                  />

                  {/* Validation Icon for Graduation Date */}
                  <div className="absolute right-[-2rem] top-5">
                    {isGraduationDateValid && selectedDate && (
                      <img src={appThemeData.Check} className="w-4 h-4" alt="Valid" />
                    )}
                    {!isGraduationDateValid && errors.graduationDate && (
                      <img src={appThemeData.ExclamationError} className="w-4 h-4" alt="Error" />
                    )}
                  </div>
                </div>
                {/* Error message for graduation date field */}
                {errors.graduationDate && (
                  <div className='mt-2'>
                    {displayErrorMessage(errors.graduationDate.message)}
                  </div>
                )}
              </div>

              <div className="flex justify-end items-center mt-18 sm:mt-20 md:mt-20 lg:mt-8 pb-8">
                <button
                  type="button"
                  className="font-hanken-grotesk sm:font-bold font-[600] lg:text-[18px] md:text-[17px] sm:text-[14px] text-[16px] text-[var(--twofac-modal-btn-clr)] cursor-pointer flex items-center"
                  onClick={GenerateEmailOtp}
                >
                  {t('continue')}
                  <img
                    src={appThemeData.SignupNextArrow}
                    className={`${signUpkudzuPlusTrue ? '' : ' md:w-[17px] sm:w-[11px]  md:h-[17px] sm:h-[11px] w-[12px] h-[12px]'} ml-2`}
                    alt=""
                  />
                </button>
              </div>
              {/* <CustomAlertModel isOpen={showAlertModal}
                onClose={closeCustomAlertModal}
                image={modelImage}
                highlightMsg={<p className={HighlightMsgStyles}>{highlightMsg}</p>}
                HeaderMsg={<p className={HeaderMsgStyles}>{headerMsg}</p>}
                subHeaderMsg={<p className={SubHeaderMsgStyles}>{subHeaderMsg}</p>}
                btn1Exists={btn1Exists}
                btn2Exists={btn2Exists}
                btn1Text={btn1Text} btn2Text={btn2Text}
                btn1TypeAcpt={btn1TypeAcpt}
                verificationFailure={() => { }}
                footerMsg={<p className={SubHeaderMsgStyles}>{footerMsg}</p>}
                onAcptBtnClick={onAcptBtnClick}
                onCancelBtnClick={onCancelBtnClick}
              /> */}
            </div>
          </div>
        </Card>
      </div>
      {isLoading && <Loader />}
    </>
  );
};

export default StudentEmailVerfication;
